import React, { useMemo, useState } from 'react';
import { RabEntry, UfsaMitgeltendeUnterlage } from '../../types';
import { LIEFERANT_TITEL } from '../../services/mitgeltendeDokumente';
import type { MitgeltendesDokument } from '../../services/mitgeltendeDokumente';
import {
    aktualisiereUnterlage,
    entferneUnterlage,
    fuegeUnterlageHinzu,
    mitgeltendeUnterlagenListe,
    schlageUnterlagenVor,
    uebernehmeDokument,
    verschiebeUnterlage,
} from './ufsaMitgeltendeUnterlagen';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Info, Library, Lightbulb, Lock, Plus, Trash2 } from 'lucide-react';

interface Props {
    unterlagen: UfsaMitgeltendeUnterlage[] | undefined;
    entries: RabEntry[];
    /** Neue Liste — läuft über projectData und damit über das Autospeichern der Ansicht. */
    onChange: (liste: UfsaMitgeltendeUnterlage[]) => void;
    isReadOnly?: boolean;
}

const inputClass = 'w-full p-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-600';

/** Höchstens drei Anlässe ausgeschrieben, der Rest als Anzahl. */
const anlassText = (anlaesse: string[]): string =>
    anlaesse.length <= 3 ? anlaesse.join(', ') : `${anlaesse.slice(0, 3).join(', ')} und ${anlaesse.length - 3} weitere`;

interface VorschlagProps {
    dokument: MitgeltendesDokument;
    anlaesse?: string[];
    bedingungen?: string[];
    offen: boolean;
    onToggle: () => void;
    onUebernehmen: () => void;
}

const Vorschlag: React.FC<VorschlagProps> = ({ dokument, anlaesse, bedingungen, offen, onToggle, onUebernehmen }) => (
    <div className="border border-slate-200 rounded-lg bg-white">
        <div className="flex items-start justify-between gap-3 p-3">
            <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 break-words">
                    {dokument.name}
                    {dokument.kurz && <span className="ml-1.5 text-xs font-medium text-slate-400">({dokument.kurz})</span>}
                </div>
                {anlaesse && anlaesse.length > 0 && (
                    <div className="text-xs text-slate-500 mt-0.5 break-words">zu: {anlassText(anlaesse)}</div>
                )}
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={offen}
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                    {offen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <Info className="w-3.5 h-3.5" /> Erklärung
                </button>
            </div>
            <button
                type="button"
                onClick={onUebernehmen}
                title={`„${dokument.name}“ in die Liste übernehmen`}
                className="shrink-0 px-2.5 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md inline-flex items-center gap-1.5 transition-colors"
            >
                <Plus className="w-3.5 h-3.5" /> Übernehmen
            </button>
        </div>
        {offen && (
            <dl className="px-3 pb-3 pt-2 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                <div>
                    <dt className="font-bold text-slate-500 uppercase text-[11px]">Wann benötigt</dt>
                    <dd className="mt-0.5">{dokument.wannBenoetigt}</dd>
                </div>
                <div>
                    <dt className="font-bold text-slate-500 uppercase text-[11px]">Für den SiGeKo relevant</dt>
                    <dd className="mt-0.5">{dokument.sigekoRelevanz}</dd>
                </div>
                {dokument.lieferant.length > 0 && (
                    <div>
                        <dt className="font-bold text-slate-500 uppercase text-[11px]">Liefert</dt>
                        <dd className="mt-0.5">{dokument.lieferant.map(l => LIEFERANT_TITEL[l] || l).join(', ')}</dd>
                    </div>
                )}
                {dokument.rechtsgrundlage && dokument.rechtsgrundlage.length > 0 && (
                    <div>
                        <dt className="font-bold text-slate-500 uppercase text-[11px]">Grundlage</dt>
                        <dd className="mt-0.5">{dokument.rechtsgrundlage.join('; ')}</dd>
                    </div>
                )}
                {bedingungen && bedingungen.length > 0 && (
                    <div>
                        <dt className="font-bold text-slate-500 uppercase text-[11px]">Hinweis</dt>
                        <dd className="mt-0.5">{bedingungen.join(' ')}</dd>
                    </div>
                )}
            </dl>
        )}
    </div>
);

/**
 * Abschnitt „Mitgeltende Unterlagen" der Unterlage für spätere Arbeiten:
 * Liste mit Ablageort beim Betreiber, Vorschläge aus dem Katalog der
 * mitgeltenden Dokumente (passend zum Maßnahmenkatalog und weitere übliche),
 * freier Eintrag. Im PDF ein eigenes Kapitel nach dem Maßnahmenkatalog.
 */
export const MitgeltendeUnterlagenPanel: React.FC<Props> = ({ unterlagen, entries, onChange, isReadOnly = false }) => {
    const liste = mitgeltendeUnterlagenListe(unterlagen);
    const [neuerName, setNeuerName] = useState('');
    const [hinweis, setHinweis] = useState<string | null>(null);
    const [offeneErklaerungen, setOffeneErklaerungen] = useState<Set<string>>(new Set());
    const [weitereOffen, setWeitereOffen] = useState(false);

    // Abhängig von der gespeicherten Referenz, nicht von `liste` — die ist
    // bei fehlendem Feld in jedem Render ein neues leeres Array.
    const { passend, weitere } = useMemo(() => schlageUnterlagenVor(entries, unterlagen), [entries, unterlagen]);

    const aendern = (next: UfsaMitgeltendeUnterlage[]) => {
        if (isReadOnly || next === liste) return;
        onChange(next);
    };

    const toggleErklaerung = (id: string) => {
        setOffeneErklaerungen(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const hinzufuegen = () => {
        const name = neuerName.trim();
        if (!name) return;
        const next = fuegeUnterlageHinzu(liste, name, crypto.randomUUID());
        if (next === liste) {
            setHinweis(`„${name}“ steht bereits in der Liste.`);
            return;
        }
        aendern(next);
        setNeuerName('');
        setHinweis(null);
    };

    return (
        <section id="ufsa-mitgeltende-unterlagen" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600 shrink-0">
                    <Library className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        Mitgeltende Unterlagen
                        <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200 font-medium">{liste.length}</span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Unterlagen, die beim Betreiber vorliegen und bei späteren Arbeiten heranzuziehen sind (Statik, Revisionspläne, Prüfbücher …). Im PDF ein eigenes Kapitel nach dem Maßnahmenkatalog.
                    </p>
                </div>
            </div>

            <div className="p-5 space-y-6">
                {isReadOnly && (
                    <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <Lock className="w-3.5 h-3.5 shrink-0" /> Nur-Ansicht: die Liste kann in diesem Projekt nicht geändert werden.
                    </div>
                )}

                {liste.length === 0 ? (
                    <div className="text-center py-6 px-4 rounded-lg border border-dashed border-slate-200 text-sm text-slate-500">
                        Noch keine Unterlagen erfasst.{!isReadOnly && ' Übernehmen Sie Vorschläge unten oder tragen Sie eine Unterlage frei ein.'}
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className="hidden md:grid md:grid-cols-12 gap-2 px-1 text-[11px] font-bold text-slate-500 uppercase">
                            <div className="col-span-4">Unterlage</div>
                            <div className="col-span-4">Ablageort beim Betreiber</div>
                            <div className={isReadOnly ? 'col-span-4' : 'col-span-3'}>Bemerkung</div>
                        </div>
                        {liste.map((u, i) => (
                            <div key={u.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start p-2 rounded-lg border border-slate-100 bg-slate-50/60 hover:border-slate-200">
                                <div className="md:col-span-4">
                                    <label className="md:hidden text-[11px] font-bold text-slate-500 uppercase">Unterlage</label>
                                    <input
                                        value={u.name}
                                        onChange={e => aendern(aktualisiereUnterlage(liste, u.id, { name: e.target.value }))}
                                        disabled={isReadOnly}
                                        aria-label="Unterlage"
                                        placeholder="Bezeichnung der Unterlage"
                                        className={`${inputClass} font-medium ${u.name.trim() ? '' : 'border-red-300'}`}
                                    />
                                </div>
                                <div className="md:col-span-4">
                                    <label className="md:hidden text-[11px] font-bold text-slate-500 uppercase">Ablageort beim Betreiber</label>
                                    <input
                                        value={u.ablageort}
                                        onChange={e => aendern(aktualisiereUnterlage(liste, u.id, { ablageort: e.target.value }))}
                                        disabled={isReadOnly}
                                        aria-label={`Ablageort von ${u.name}`}
                                        placeholder="z. B. Hausmeisterbüro, Ordner Statik"
                                        className={inputClass}
                                    />
                                </div>
                                <div className={isReadOnly ? 'md:col-span-4' : 'md:col-span-3'}>
                                    <label className="md:hidden text-[11px] font-bold text-slate-500 uppercase">Bemerkung</label>
                                    <input
                                        value={u.bemerkung}
                                        onChange={e => aendern(aktualisiereUnterlage(liste, u.id, { bemerkung: e.target.value }))}
                                        disabled={isReadOnly}
                                        aria-label={`Bemerkung zu ${u.name}`}
                                        placeholder="z. B. Stand Abnahme"
                                        className={inputClass}
                                    />
                                </div>
                                {!isReadOnly && (
                                    <div className="md:col-span-1 flex items-center justify-end gap-0.5 md:pt-1">
                                        <button
                                            type="button"
                                            onClick={() => aendern(verschiebeUnterlage(liste, u.id, 'up'))}
                                            disabled={i === 0}
                                            title="Nach oben"
                                            aria-label={`${u.name} nach oben`}
                                            className={`p-1.5 rounded ${i === 0 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:text-slate-800 hover:bg-slate-200'}`}
                                        >
                                            <ArrowUp className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => aendern(verschiebeUnterlage(liste, u.id, 'down'))}
                                            disabled={i === liste.length - 1}
                                            title="Nach unten"
                                            aria-label={`${u.name} nach unten`}
                                            className={`p-1.5 rounded ${i === liste.length - 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:text-slate-800 hover:bg-slate-200'}`}
                                        >
                                            <ArrowDown className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => aendern(entferneUnterlage(liste, u.id))}
                                            title="Aus der Liste entfernen"
                                            aria-label={`${u.name} entfernen`}
                                            className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-100"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!isReadOnly && (
                    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                        <label htmlFor="ufsa-mitgeltend-neu" className="text-xs font-bold text-slate-600 uppercase flex items-center gap-1.5 mb-2">
                            <Plus className="w-3.5 h-3.5" /> Unterlage frei eintragen
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                id="ufsa-mitgeltend-neu"
                                value={neuerName}
                                onChange={e => { setNeuerName(e.target.value); setHinweis(null); }}
                                onKeyDown={e => { if (e.key === 'Enter') hinzufuegen(); }}
                                placeholder="z. B. Wartungsvertrag Lüftungsanlage"
                                className={`${inputClass} bg-white`}
                            />
                            <button
                                type="button"
                                onClick={hinzufuegen}
                                disabled={!neuerName.trim()}
                                className="shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition-colors"
                            >
                                Hinzufügen
                            </button>
                        </div>
                        {hinweis && <p className="text-xs text-amber-700 mt-2">{hinweis}</p>}
                    </div>
                )}

                {!isReadOnly && (
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-500" /> Vorschläge aus dem Maßnahmenkatalog
                        </h4>
                        {passend.length === 0 ? (
                            <p className="text-xs text-slate-500">
                                {entries.length === 0
                                    ? 'Sobald der Maßnahmenkatalog Einträge hat, erscheinen hier die dazu passenden Unterlagen.'
                                    : 'Zu den erfassten Maßnahmen gibt es keine weiteren Vorschläge.'}
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                                {passend.map(v => (
                                    <Vorschlag
                                        key={v.dokument.id}
                                        dokument={v.dokument}
                                        anlaesse={v.anlaesse}
                                        bedingungen={v.bedingungen}
                                        offen={offeneErklaerungen.has(v.dokument.id)}
                                        onToggle={() => toggleErklaerung(v.dokument.id)}
                                        onUebernehmen={() => aendern(uebernehmeDokument(liste, v.dokument, crypto.randomUUID()))}
                                    />
                                ))}
                            </div>
                        )}

                        {weitere.length > 0 && (
                            <div className="pt-2">
                                <button
                                    type="button"
                                    onClick={() => setWeitereOffen(o => !o)}
                                    aria-expanded={weitereOffen}
                                    className="text-sm font-bold text-slate-600 hover:text-blue-600 inline-flex items-center gap-1.5"
                                >
                                    {weitereOffen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    Weitere übliche Unterlagen ({weitere.length})
                                </button>
                                {weitereOffen && (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
                                        {weitere.map(d => (
                                            <Vorschlag
                                                key={d.id}
                                                dokument={d}
                                                offen={offeneErklaerungen.has(d.id)}
                                                onToggle={() => toggleErklaerung(d.id)}
                                                onUebernehmen={() => aendern(uebernehmeDokument(liste, d, crypto.randomUUID()))}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};
